const INVALID_INPUT_ERROR = "Invalid inputs";

/**
 * Returns the larger and smaller of two arrays based on their lengths.
 *
 * @param {Array} ds1 - First array to compare.
 * @param {Array} ds2 - Second array to compare.
 * @returns {{ largest: Array, smallest: Array }}
 *   - `largest`: The array with more elements.
 *   - `smallest`: The array with fewer elements.
 *     If both are of equal length, `ds2` is considered the larger.
 *
 * @throws {Error} If either input is not an array.
 */
export const getLargestAndSmallestDataSet = (ds1 = [], ds2 = []) => {
    if(!Array.isArray(ds1) || !Array.isArray(ds2)) {
        throw new Error(INVALID_INPUT_ERROR);
    }
    return ds1.length > ds2.length
        ? {largest: ds1, smallest: ds2}
        : {largest: ds2, smallest: ds1};
};

/**
 * Compares two arrays (ISV fields and dealer-selected fields) to determine:
 * - Fields that are common to both
 * - Fields that are present in one but not the other
 *
 * @param {Array} isvFields - The list of fields provided by ISV (e.g. available fields).
 * @param {Array} dealerSelection - The list of fields selected/configured by the dealer.
 * @returns {{ commonFields: Array, nonCommonFields: Array }}
 *   - commonFields: Fields present in both arrays
 *   - nonCommonFields: Fields present only in one of the arrays
 *
 * @throws {Error} If inputs are not arrays
 */
export const getOverlappingFields = (isvFields = [], dealerSelection = []) => {
    if(!Array.isArray(isvFields) || !Array.isArray(dealerSelection)) {
        throw new Error(INVALID_INPUT_ERROR);
    }

    const {largest, smallest} = getLargestAndSmallestDataSet(isvFields, dealerSelection);
    const fieldSet = new Set(smallest);

    const commonFields = [];
    const nonCommonFields = [];

    for(const field of largest) {
        (fieldSet.has(field) ? commonFields : nonCommonFields).push(field);
    }

    return { commonFields, nonCommonFields};
};